import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router';
import dayjs from 'dayjs';
import { useAuth } from '../../context/AuthContext';
import Api from '../../services/api';
import * as mappings from '../../utils/mappings';
// componentes:
import {
    Row,
    Flex,
    Spin,
    Result,
    Divider,
    Timeline,
    Tooltip,
    Typography,
    Button,
    Space,
    FloatButton,
    Modal,
    Checkbox,
    Alert,
    notification,
    Form,
} from 'antd';
import {
    FileProtectOutlined,
    CheckCircleOutlined,
    LoadingOutlined,
    MailOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
} from '@ant-design/icons';
import ReportTemplateForm from './components/ReportTemplateForm';
import ReportFormHeader from './components/ReportFormHeader';
import ReportComplements from './components/ReportComplements';
import { Centered } from '../../components';

// os gabinetes habilitados a interagir com o relatório:
const officesAllowedToInteract = [
    'lotacao:primeira_subcorregedoria_geral',
    'lotacao:segunda_subcorregedoria_geral',
]

function ReportForm() {
    const [form] = Form.useForm();
    const [api, contextHolder] = notification.useNotification({
        placement: 'top',
        maxCount: 2,
        duration: 5
    });

    const { id } = useParams();
    const { authUserBelongsToGroups } = useAuth();
    const [isFetching, setIsFetching] = useState(false);
    const [isSendingNotification, setIsSendingNotification] = useState(false);
    const [isSendingToRevision, setIsSendingToRevision] = useState(false);
    const [isSendingApproval, setIsSendingApproval] = useState(false);
    const [report, setReport] = useState(null);
    const [sortedHistoryItems, setSortedHistoryItems] = useState([]);
    const [lastHistory, setLastHistory] = useState({});
    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [showNotificationModal, setShowNotificationModal] = useState(false);
    const [approvalKnowledge, setApprovalKnowledge] = useState(false);
    const [responseStatusError, setResponseStatusError] = useState(null);
    const topRef = useRef(null);
    const bottomRef = useRef(null);

    /**
     * Recupera os atributos do relatório especificado.
     * 
     * @param {Number} reportId 
     */
    async function fetchReport(reportId) {
        setIsFetching(true);

        try {
            const response = await Api('reports').show(reportId);

            setReport(response.data);
            
            const sorted = [...response.data.history].sort((previous, current) =>
                new Date(previous.created_at) > new Date(current.created_at) ? 0 : -1
            );
        
            setSortedHistoryItems(sorted);
            setLastHistory(sorted[sorted.length - 1]);
        } catch (error) {
            setResponseStatusError(error.status);
        }

        setIsFetching(false);
    }

    useEffect(() => {
        fetchReport(id);
    }, [id]);

    useEffect(() => {
        if (report?.history) {
            const sorted = [...report.history].sort((previous, current) =>
                new Date(previous.created_at) > new Date(current.created_at) ? 0 : -1
            );

            setSortedHistoryItems(sorted);
            setLastHistory(sorted[sorted.length - 1]);
        }
    }, [report?.history]);

    if (!report) {
        if (isFetching) {
            return (
                <Centered>
                    <Space direction="vertical" align="center" size="large">
                        <Spin indicator={<LoadingOutlined spin />} size="large" />
    
                        <Typography.Title level={5}>
                            Em alguns instantes os dados do relatório aparecerão aqui.
                        </Typography.Title>
                    </Space>
                </Centered>
            );
        }

        return (
            <Centered>
                {responseStatusError ? (
                    <Result
                        status={responseStatusError}
                        subTitle={
                            <Typography.Title level={4}>
                                Não foi possível carregar este recurso.
                            </Typography.Title>
                        }
                        extra={
                            <Typography.Text>
                                Se acredita ser um erro, entre em contato com <a href="mailto:suportesistemas@dp.mt.gov.br">suportesistemas@dp.mt.gov.br</a>
                            </Typography.Text>
                        }
                    />
                ) : (
                    <Result
                        status="404"
                        title="404"
                        subTitle="Não foi possível localizar o relatório especificado."
                    />
                )}
            </Centered>
        );
    }

    // os mesmos grupos de usuários que podem aprovar, podem
    // notificar o defensor interessado, exceto se este relatório
    // já tiver sido homologado:
    const canAuthUserNotify = (
        authUserBelongsToGroups([
            'superadministrador',
            'administrador',
            ...officesAllowedToInteract
        ])
        && lastHistory.status.slug !== mappings.status.RASCUNHO
    );

    /**
     * Envia uma requisição para submeter o relatório
     * para correção.
     */
    const handleSendToRevision = async () => {
        setIsSendingToRevision(true);

        try {
            const response = await Api(`/reports/${report.id}/revision`).store();

            setReport(prevState => ({
                ...prevState,
                history: [
                    ...prevState.history,
                    response.data
                ]
            }))
        } catch {}

        setIsSendingToRevision(false);
    }

    const ApprovalButton = () => (
        <Space>
            Pronto para homologar

            <Button
                size="small"
                type="primary"
                onClick={() => {
                    form.validateFields()
                        .then(() => setShowApprovalModal(true))
                        .catch(() => {
                            api.error({
                                message: 'Há campos obrigatórios pendentes',
                                description: 'Preencha os campos antes de homologar.'
                            });
                        });
                }}
                loading={showApprovalModal}
            >
                homologar
            </Button>
        </Space>
    );

    const RevisionButton = () => (
        <Space size="middle">
            <Flex vertical>
                <Typography.Text>Pronto para correção</Typography.Text>
                <Typography.Text type="secondary">
                    <small>Enviar para correção pelo(a) Subcorregedor(a).</small>
                </Typography.Text>
            </Flex>

            <Button
                size="small"
                type="primary"
                onClick={handleSendToRevision}
                loading={isSendingToRevision}
            >
                enviar
            </Button>
        </Space>
    );

    function authUserActionByStatusSlug(slug) {
        const isReportDraft = lastHistory.status.slug === mappings.status.RASCUNHO;
        const isReportApproved = report.history.filter(
            record => record.status.slug === mappings.status.HOMOLOGADO
        ).length;

        // não haverão ações para relatórios em rascunho ou homologados:
        if (isReportApproved) {
            return;
        }

        // não haverão ações para relatórios em rascunho:
        if (isReportDraft) {
            return 'Aguardando preenchimento';
        }
        
        // não haverão ações para relatórios com pedidos
        // de manifestação pendentes:
        if (report.has_pending_submissions) {
            return 'Aguardando manifestação';
        }

        // as ações possíveis para os assessores:
        if (authUserBelongsToGroups(['assessor'])) {
            // os estados em que o relatório pode ser
            // submetido para correção:
            const statusAllowedToRevision = [
                mappings.status.EDITADO,
                mappings.status.NOTIFICADO,
                mappings.status.FALHA_NA_NOTIFICACAO,
                // ...
            ]

            if (statusAllowedToRevision.includes(slug)) {
                return <RevisionButton />;
            }

            return;
        }

        // os grupos habilitados à interação:
        const isAdministrator = authUserBelongsToGroups([
            ...officesAllowedToInteract,
            'superadministrador',
            'administrador',
        ])

        // as ações possíveis para os administradores:
        if (isAdministrator) {
            return <ApprovalButton />;
        }
    }

    const historyAttributesByStatusSlug = {
        [mappings.status.RASCUNHO]: {
            pending: authUserActionByStatusSlug(mappings.status.RASCUNHO),
            color: 'grey',
        },
        [mappings.status.NOTIFICADO]: {
            pending: authUserActionByStatusSlug(mappings.status.NOTIFICADO),
        },
        [mappings.status.FALHA_NA_NOTIFICACAO]: {
            pending: authUserActionByStatusSlug(mappings.status.FALHA_NA_NOTIFICACAO),
            color: 'red'
        },
        [mappings.status.AGUARDANDO_CORRECAO]: {
            pending: authUserActionByStatusSlug(mappings.status.AGUARDANDO_CORRECAO),
            color: 'purple'
        },
        [mappings.status.EDITADO]: {
            pending: authUserActionByStatusSlug(mappings.status.EDITADO),
            color: 'orange',
        },
        [mappings.status.AGUARDANDO_MANIFESTACAO]: {
            pending: authUserActionByStatusSlug(mappings.status.AGUARDANDO_MANIFESTACAO),
            color: 'grey'
        },
        [mappings.status.MANIFESTACAO_RECEBIDA]: {
            pending: authUserActionByStatusSlug(mappings.status.MANIFESTACAO_RECEBIDA),
            color: 'black'
        },
        [mappings.status.HOMOLOGADO]: {
            color: 'green'
        },
    };

    /**
     * Mapeia os itens do histórico do relatório
     * para os elementos da timeline. 
     * 
     * @param {Object} row 
     * @returns {Object}
     */
    const toTimelineItem = row => {
        // as preposicoes por status:
        const prepositions = {
            [mappings.status.MANIFESTACAO_RECEBIDA]: 'de',
            // ...
        }

        return {
            key: row.id,
            label: dayjs(row?.updated_at).format('DD/MM/YYYY HH:mm'),
            children: (
                <Tooltip title={row.status.description}>
                    <p>
                        <b>{row.status.name}</b><br/>
                        {prepositions[row.status.slug] ?? 'por'} {row.user.full_name}
                    </p>

                    {row.description && (
                        <Typography.Text type="secondary">
                            <small>{row.description}</small>
                        </Typography.Text>
                    )}
                </Tooltip>
            ),
            color: historyAttributesByStatusSlug[row.status.slug]?.color
        }
    }

    /**
     * Envia uma requisição para homologar o
     * relatório.
     */
    const handleReportApproval = async () => {
        setIsSendingApproval(true);

        try {
            const response = await Api(`/reports/${report.id}/approve`).store({});

            for (const index in response.data) {
                const record = response.data[index];
                const notificationType = record.status.slug.includes('falha_') ? 'error' : 'success';
                
                api[notificationType]({
                    message: record.status.name,
                    description: record.description
                });
            }
            
            setReport(prevState => ({
                ...prevState,
                history: [
                    ...prevState.history,
                    ...response.data
                ]
            }));

            setShowApprovalModal(false);
        } catch (error) {
            const responseData = error.response?.data;
            const errorMessage = typeof responseData === 'string'
                ? responseData
                : responseData?.detail ?? responseData?.message ?? '';
            const requiredQuestions = errorMessage.match(
                /As seguintes perguntas obrigatórias não foram respondidas:\s*([^<\n]+?)(?:\.|<|$)/i
            );

            api.error({
                message: 'Há campos obrigatórios pendentes',
                description: requiredQuestions
                    ? `Preencha os campos antes de homologar: ${requiredQuestions[1].trim()}.`
                    : 'Não foi possível homologar o relatório. Tente novamente.'
            });
        } finally {
            setIsSendingApproval(false);
        }
    };

    /**
     * Envia uma requisição para notificar o
     * defensor interessado por e-mail.
     */
    const handleNotify = async () => {
        setIsSendingNotification(true);
        
        try {
            const { data: nextHistory } = await Api(`reports/${report.id}/notify`).store({});
            const notificationType = nextHistory[0].status.slug.includes('falha_') ? 'error' : 'success';

            api[notificationType]({
                message: nextHistory[0].status.name,
                description: nextHistory[0].description
            });

            setReport(prevState => ({
                ...prevState,
                history: [
                    ...nextHistory,
                    ...prevState.history
                ]
            }));
        } catch {}
        
        setShowNotificationModal(false);
        setIsSendingNotification(false);
    };

    return (
        <>
            {contextHolder}

            {report && (
                <>
                    {/* Ancora para o início da página */}
                    <div ref={topRef}></div>

                    {report.is_test && (
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            position: 'fixed',
                            bottom: 0,
                            left: 0,
                            width: '100%',
                            height: 50,
                            backgroundColor: '#1677ff',
                            color: 'white',
                            fontSize: 18,
                            opacity: 0.9,
                            zIndex: 9999
                        }}>
                            <span>🧪 Este é um relatório <b>experimental</b> e sem validade.</span>
                        </div>
                    )}

                    <ReportFormHeader
                        templateName={report.template.name}
                        reportStatus={lastHistory.status}
                    />

                    <ReportTemplateForm
                        report={report}
                        setReport={setReport}
                        form={form}
                        readOnly={report.is_test || report.history.filter(record => record.status.slug === mappings.status.HOMOLOGADO).length}
                    />

                    {report.history.filter(record => record.status.slug === mappings.status.HOMOLOGADO).length > 0 && (
                        <>
                            <Divider style={{ marginTop: '3.5rem', marginBottom: '2.5rem' }}>Anexos</Divider>

                            <ReportComplements report={report} setReport={setReport} />
                        </>
                    )}

                    {!report.is_test && (
                        <>
                            <Divider style={{ marginTop: '3.5rem', marginBottom: '2.5rem' }}>
                                Histórico
                            </Divider>

                            <Timeline
                                mode="left"
                                items={sortedHistoryItems.map(toTimelineItem)}
                                pending={historyAttributesByStatusSlug[lastHistory.status.slug]?.pending}
                                reverse
                            />
                        </>
                    )}

                    <Modal
                        centered
                        open={showApprovalModal}
                        title={(<><FileProtectOutlined /> Homologar RMA</>)}
                        footer={(
                            <Row justify="space-between">
                                <Flex vertical>
                                    <Space>
                                        <Checkbox
                                            onChange={({ target }) => setApprovalKnowledge(target.checked)}
                                            checked={approvalKnowledge}
                                        />
                                        
                                        Estou ciente e desejo homologar.
                                    </Space>
                                </Flex>

                                <Button
                                    type="primary"
                                    icon={<CheckCircleOutlined />}
                                    disabled={!approvalKnowledge}
                                    onClick={handleReportApproval}
                                    loading={isSendingApproval}
                                >
                                    Homologar
                                </Button>
                            </Row>
                        )}
                        onCancel={() => {
                            setShowApprovalModal(false);
                            setApprovalKnowledge(false);
                        }}
                    >
                        <Divider />

                        <Space style={{ marginBottom: '0.5rem' }} direction="vertical">
                            <Typography.Text>
                                Deseja homologar o RMA de <b>{report.defender.user.full_name}</b>?
                            </Typography.Text>

                            <Alert
                                type="error"
                                message={
                                    <>
                                        Ao confirmar, este relatório ficará disponível apenas para <b>LEITURA</b>, e não poderá ser modificado ou excluído.
                                    </>
                                }
                            />
                        </Space>
                    </Modal>

                    <Modal
                        centered
                        open={showNotificationModal}
                        onCancel={() => setShowNotificationModal(false)}
                        title={(<><MailOutlined /> Notificar {report.defender.user.first_name}</>)}
                        footer={(
                            <Flex justify="space-between">
                                <Button onClick={() => setShowNotificationModal(false)}>Cancelar</Button>

                                {report.defender.user.email && (
                                    <Button
                                        type="primary" 
                                        onClick={handleNotify}
                                        loading={isSendingNotification}
                                    >
                                        Sim, enviar
                                    </Button>
                                )}
                            </Flex>
                        )}
                    >
                        <Divider />

                        {report.defender.user.email ? (
                            <Space direction="vertical">
                                {report.history.filter(({ status }) => status.slug === mappings.status.HOMOLOGADO).length ? (
                                    <Typography.Text>
                                        Deseja <b>reenviar o RMA homologado</b>, para o e-mail de {report.defender.user.full_name}?:
                                    </Typography.Text>
                                ) : (
                                    <Typography.Text>
                                        Deseja enviar um pedido de <b>MANIFESTAÇÃO</b> sobre este RMA, para o e-mail de {report.defender.user.full_name}?
                                    </Typography.Text>
                                )}

                                <Typography.Text>
                                    <a href={`mailto:${report.defender.user.email}`}>{report.defender.user.email}</a>
                                </Typography.Text>
                            </Space>
                        ) : (
                            <Alert
                                banner
                                type="error"
                                description={(
                                    <Typography.Text>
                                        Nenhum e-mail registrado para {report.defender.user.full_name}.
                                    </Typography.Text>
                                )}
                            />
                        )}
                    </Modal>

                    {/* Ancora para o final da página */}
                    <div ref={bottomRef}></div>

                    <FloatButton.Group shape="square">
                        <Tooltip title="Início da Página" placement="left">
                            <FloatButton
                                icon={<ArrowUpOutlined />}
                                onClick={() => {
                                    topRef.current.scrollIntoView({
                                        behavior: "smooth",
                                        block: "start",
                                        inline: "center",
                                    });
                                }}
                            />
                        </Tooltip>

                        {canAuthUserNotify && (
                            <Tooltip title={`Notificar ${report.defender.user.first_name}`} placement="left">
                                <FloatButton
                                    type="primary"
                                    badge={{ dot: !report.defender.user.email }}
                                    icon={isSendingNotification ? <LoadingOutlined /> : <MailOutlined />}
                                    onClick={() => setShowNotificationModal(true)}
                                />
                            </Tooltip>
                        )}

                        {!report.is_test && (
                            <Tooltip title="Visualizar em PDF" placement="left">
                                <FloatButton
                                    type="primary"
                                    onClick={() => {
                                        window.open(`${process.env.REACT_APP_API_URL}/api/v1/reports/${report.id}/download`, '_blank')
                                    }}
                                />
                            </Tooltip>
                        )}

                        <Tooltip title="Final da Página" placement="left">
                            <FloatButton
                                icon={<ArrowDownOutlined />}
                                onClick={() => {
                                    bottomRef.current.scrollIntoView({
                                        behavior: "smooth",
                                        block: "start",
                                        inline: "center",
                                    });
                                }}
                            />
                        </Tooltip>
                    </FloatButton.Group>
                </>
            )}
        </>
    );
}

export default ReportForm;
